import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import SEO from "../components/SEO";
import { nitrologo } from "../utilities/images";
import { db } from "../firebase";
import { ref, update } from "firebase/database";
import { updateProfile } from "firebase/auth";
import "./Settings.css";

function Settings() {
  const auth = useAuth();
  const user = auth?.currentUser;
  const rider = auth?.riderData;
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState("home");
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Inline edit state toggles
  const [editingField, setEditingField] = useState(null); // 'name', 'phone', 'places', 'password'

  // Personal Info state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [homeAddress, setHomeAddress] = useState("");
  const [workAddress, setWorkAddress] = useState("");

  // Security state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  // Privacy & Data state
  const [shareDiagnostics, setShareDiagnostics] = useState(true);
  const [personalizedOffers, setPersonalizedOffers] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [supportModalData, setSupportModalData] = useState(null);
  // Phone Modal state
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [tempPhone, setTempPhone] = useState("");

  // Status indicators
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const openPhoneModal = () => {
    let cleanNum = (phone || "").replace(/^\+234\s?/, "").replace(/\D/g, "");
    setTempPhone(cleanNum);
    setShowPhoneModal(true);
  };

  const handleSavePhone = async (e) => {
    e.preventDefault();
    if (!user || !tempPhone) return;
    setIsSaving(true);
    try {
      const formattedPhone = `+234${tempPhone.trim()}`;
      setPhone(formattedPhone);

      const riderRef = ref(db, `riders/${user.uid}`);
      await update(riderRef, {
        phoneNumber: formattedPhone,
        phone: formattedPhone,
        updated_at: new Date().toISOString(),
      });

      setSaveSuccess(true);
      setShowPhoneModal(false);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err) {
      console.error("Failed to update phone number:", err);
      setErrorMessage("Failed to update phone number.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show instant local preview
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);

    // Convert file to Base64 data URL for storing in Firebase
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Data = reader.result;
      try {
        if (auth?.currentUser) {
          await updateProfile(auth.currentUser, { photoURL: base64Data });
          const riderRef = ref(db, `riders/${auth.currentUser.uid}`);
          await update(riderRef, {
            photo_url: base64Data,
            updated_at: new Date().toISOString(),
          });
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 4000);
        }
      } catch (err) {
        console.error("Failed to update profile photo:", err);
      }
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (rider) {
      setFirstName(rider.first_name || "");
      setLastName(rider.last_name || "");
      setPhone(rider.phoneNumber || rider.phone || "");

      // Saved Locations
      const hAddr =
        rider.savedLocations?.home?.address ||
        rider.savedLocations?.home?.title ||
        rider.home_address ||
        "";
      const wAddr =
        rider.savedLocations?.work?.address ||
        rider.savedLocations?.work?.title ||
        rider.work_address ||
        "";
      setHomeAddress(hAddr);
      setWorkAddress(wAddr);

      // Security / Safety Settings
      if (rider.safety_settings?.pin_verification !== undefined) {
        setTwoFactorEnabled(rider.safety_settings.pin_verification);
      } else if (rider.security_settings?.two_factor !== undefined) {
        setTwoFactorEnabled(rider.security_settings.two_factor);
      }

      // Notifications / Privacy Settings
      if (rider.notification_settings) {
        setShareDiagnostics(
          rider.notification_settings.system_notifications_enabled ?? true
        );
        setPersonalizedOffers(
          rider.notification_settings.promotions_email ?? true
        );
      } else if (rider.privacy_settings) {
        setShareDiagnostics(rider.privacy_settings.share_diagnostics ?? true);
        setPersonalizedOffers(rider.privacy_settings.personalized_offers ?? true);
      }
    } else if (user) {
      const names = (user.displayName || "").split(" ");
      setFirstName(names[0] || "");
      setLastName(names.slice(1).join(" ") || "");
    }
    if (user) {
      setEmail(user.email || rider?.email || "");
    }
  }, [user, rider]);



  const handleSaveSecurity = async (e) => {
    if (e) e.preventDefault();
    if (!user) return;

    if (newPassword && newPassword !== confirmPassword) {
      setErrorMessage("New passwords do not match.");
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);
    setErrorMessage("");

    try {
      const riderRef = ref(db, `riders/${user.uid}`);
      await update(riderRef, {
        "safety_settings/pin_verification": twoFactorEnabled,
        "security_settings/two_factor": twoFactorEnabled,
        updated_at: new Date().toISOString(),
      });

      setSaveSuccess(true);
      setEditingField(null);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err) {
      console.error("Failed to update security settings:", err);
      setErrorMessage("Failed to update security settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePrivacyToggle = async (updatedDiagnostics, updatedOffers) => {
    if (!user) return;
    try {
      const riderRef = ref(db, `riders/${user.uid}`);
      await update(riderRef, {
        "notification_settings/system_notifications_enabled": updatedDiagnostics,
        "notification_settings/promotions_email": updatedOffers,
        "privacy_settings/share_diagnostics": updatedDiagnostics,
        "privacy_settings/personalized_offers": updatedOffers,
        updated_at: new Date().toISOString(),
      });
    } catch (err) {
      console.error("Failed to update privacy settings:", err);
    }
  };

  const handleDownloadData = () => {
    const dataToDownload = {
      user: {
        uid: user?.uid,
        email: user?.email,
        displayName: user?.displayName,
      },
      profile: {
        first_name: firstName,
        last_name: lastName,
        phone: phone,
        home_address: homeAddress,
        work_address: workAddress,
      },
      exported_at: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(dataToDownload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `9tro_ride_account_data_${user?.uid || "rider"}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const avatarName =
    firstName && lastName
      ? `${firstName} ${lastName}`
      : firstName || user?.displayName || "Rider";

  return (
    <div className="nitro-settings-page">
      <SEO
        title="Account Settings - 9tro Ride"
        description="Manage your 9tro Ride account details, personal info, security, and privacy data."
      />
      <header className="nitro-navbar">
        <div className="nitro-navbar-container">
          <Link to="/" className="nitro-logo-link">
            <img src={nitrologo} alt="9troride" className="nitro-navbar-logo" />
          </Link>
        </div>
      </header>

      <div className="nitro-container">
        <div className="nitro-layout">
          {/* Left Sidebar */}
          <aside className="nitro-sidebar">
            <nav className="nitro-menu">
              <button
                className={`nitro-menu-item ${
                  activeTab === "home" ? "active" : ""
                }`}
                onClick={() => {
                  setActiveTab("home");
                  setEditingField(null);
                }}
              >
                <span>Home</span>
              </button>

              <button
                className={`nitro-menu-item ${
                  activeTab === "personal-info" ? "active" : ""
                }`}
                onClick={() => {
                  setActiveTab("personal-info");
                  setEditingField(null);
                }}
              >
                <span>Personal info</span>
              </button>

              <button
                className={`nitro-menu-item ${
                  activeTab === "security" ? "active" : ""
                }`}
                onClick={() => {
                  setActiveTab("security");
                  setEditingField(null);
                }}
              >
                <span>Security</span>
              </button>

              <button
                className={`nitro-menu-item ${
                  activeTab === "privacy" ? "active" : ""
                }`}
                onClick={() => {
                  setActiveTab("privacy");
                  setEditingField(null);
                }}
              >
                <span>Privacy & data</span>
              </button>
            </nav>
          </aside>

          {/* Right Content */}
          <main className="nitro-content">
            {saveSuccess && (
              <div className="nitro-alert success">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                <span>Account updated successfully!</span>
              </div>
            )}

            {errorMessage && (
              <div className="nitro-alert error">
                <span>{errorMessage}</span>
              </div>
            )}

            {/* HOME TAB */}
            {activeTab === "home" && (
              <div className="nitro-tab-section">
                <div className="nitro-user-profile-header">
                  <img
                    src={
                      avatarPreview ||
                      user?.photoURL ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        avatarName
                      )}&background=8EDC06&color=000&size=128`
                    }
                    alt="Profile"
                    className="nitro-profile-avatar"
                  />
                  <div className="nitro-user-profile-details">
                    <h1>{avatarName}</h1>
                    <p>{email}</p>
                  </div>
                </div>

                <div className="home-overview-list">
                  <div className="home-overview-card" onClick={() => setActiveTab("personal-info")}>
                    <div className="overview-card-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </div>
                    <div className="overview-card-body">
                      <div className="overview-card-header">
                        <h3>Personal info</h3>
                        <span className="overview-card-badge">Profile</span>
                      </div>
                      <p>{avatarName} • {phone || email || "Add phone number"}</p>
                    </div>
                  </div>

                  <div className="home-overview-card" onClick={() => setActiveTab("security")}>
                    <div className="overview-card-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                    <div className="overview-card-body">
                      <div className="overview-card-header">
                        <h3>Security</h3>
                        <span className="overview-card-badge">{twoFactorEnabled ? "2FA Enabled" : "Protected"}</span>
                      </div>
                      <p>Password, 2-step verification, active sessions</p>
                    </div>
                  </div>

                  <div className="home-overview-card" onClick={() => setActiveTab("privacy")}>
                    <div className="overview-card-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      </svg>
                    </div>
                    <div className="overview-card-body">
                      <div className="overview-card-header">
                        <h3>Privacy & data</h3>
                        <span className="overview-card-badge">Data Controls</span>
                      </div>
                      <p>Diagnostic sharing, personalized offers & download archive</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Hidden File Input for Avatar Upload */}
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleAvatarFileChange}
              style={{ display: "none" }}
            />

            {/* PERSONAL INFO TAB */}
            {activeTab === "personal-info" && (
              <div className="nitro-tab-section">
                <div className="nitro-section-header">
                  <h1>Personal info</h1>
                  <p>Basic info and saved places used across 9tro services.</p>
                </div>

                <div className="nitro-row-cards-list">
                  {/* Profile Picture Row */}
                  <div className="nitro-row-card profile-photo-row">
                    <div className="row-card-main">
                      <div className="row-card-info">
                        <span className="row-label">Profile photo</span>
                        <span className="row-subtext">A photo helps personalize your 9tro account</span>
                      </div>
                      <div
                        className="avatar-picker-wrapper"
                        onClick={() =>
                          setSupportModalData({
                            title: "Contact support",
                            message:
                              "Photo can't be edited. Please contact us if you want to update your photo.",
                          })
                        }
                        title="Click for profile photo info"
                      >
                        <img
                          src={
                            avatarPreview ||
                            user?.photoURL ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              avatarName
                            )}&background=8EDC06&color=000&size=128`
                          }
                          alt="Profile"
                          className="personal-info-avatar"
                        />
                        <div className="avatar-camera-overlay">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 18l6-6-6-6"></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Name Row */}
                  <div
                    className="nitro-row-card"
                    onClick={() =>
                      setSupportModalData({
                        title: "Contact support",
                        message:
                          "Name can't be edited. Please contact us if you want to update your name.",
                      })
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <div className="row-card-main">
                      <div className="row-card-info">
                        <span className="row-label">Name</span>
                        <span className="row-value">{avatarName}</span>
                      </div>
                      <button className="nitro-text-btn">
                        <span className="action-chevron">›</span>
                      </button>
                    </div>
                  </div>

                  {/* Phone Row */}
                  <div
                    className="nitro-row-card"
                    onClick={openPhoneModal}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="row-card-main">
                      <div className="row-card-info">
                        <span className="row-label">Phone number</span>
                        <span className="row-value">{phone || "Add phone number"}</span>
                      </div>
                      <button className="nitro-text-btn">
                        <span className="action-chevron">›</span>
                      </button>
                    </div>
                  </div>

                  {/* Email Row */}
                  <div
                    className="nitro-row-card"
                    onClick={() =>
                      setSupportModalData({
                        title: "Contact support",
                        message:
                          "Email address can't be edited. Please contact us if you want to update your email address.",
                      })
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <div className="row-card-main">
                      <div className="row-card-info">
                        <span className="row-label">Email</span>
                        <div className="email-value-wrap">
                          <span className="row-value">{email}</span>
                          <span className="verified-pill">Verified</span>
                        </div>
                      </div>
                      <button className="nitro-text-btn">
                        <span className="action-chevron">›</span>
                      </button>
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* SECURITY TAB */}
            {activeTab === "security" && (
              <div className="nitro-tab-section">
                <div className="nitro-section-header">
                  <h1>Security</h1>
                  <p>Manage authentication options and active device sessions.</p>
                </div>

                <div className="nitro-row-cards-list">
                  {/* Password Card */}
                  <div className="nitro-row-card">
                    <div className="row-card-main">
                      <div className="row-card-info">
                        <span className="row-label">Password</span>
                        <span className="row-value">••••••••</span>
                      </div>
                      <button
                        className="nitro-text-btn"
                        onClick={() =>
                          setEditingField(
                            editingField === "password" ? null : "password"
                          )
                        }
                      >
                        {editingField === "password" ? "Cancel" : <span className="action-chevron">›</span>}
                      </button>
                    </div>

                    {editingField === "password" && (
                      <form onSubmit={handleSaveSecurity} className="row-inline-form">
                        <div className="nitro-input-group">
                          <label>Current Password</label>
                          <input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder="••••••••"
                          />
                        </div>
                        <div className="form-two-col">
                          <div className="nitro-input-group">
                            <label>New Password</label>
                            <input
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="••••••••"
                            />
                          </div>
                          <div className="nitro-input-group">
                            <label>Confirm New Password</label>
                            <input
                              type="password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              placeholder="••••••••"
                            />
                          </div>
                        </div>
                        <button type="submit" className="nitro-primary-btn" disabled={isSaving}>
                          {isSaving ? "Saving..." : "Save New Password"}
                        </button>
                      </form>
                    )}
                  </div>

                  {/* 2FA Card */}
                  <div className="nitro-row-card">
                    <div className="row-card-main">
                      <div className="row-card-info">
                        <span className="row-label">2-Step verification</span>
                        <span className="row-value">
                          {twoFactorEnabled
                            ? "Enabled — Verification code required on new logins"
                            : "Disabled — Standard authentication"}
                        </span>
                      </div>
                      <label className="nitro-switch">
                        <input
                          type="checkbox"
                          checked={twoFactorEnabled}
                          onChange={(e) => {
                            const val = e.target.checked;
                            setTwoFactorEnabled(val);
                            handleSaveSecurity();
                          }}
                        />
                        <span className="switch-slider" />
                      </label>
                    </div>
                  </div>

                  {/* Active Sessions */}
                  <div className="nitro-row-card">
                    <div className="row-card-main">
                      <div className="row-card-info">
                        <span className="row-label">Active login session</span>
                        <span className="row-value">
                          Current Web Browser • macOS • {window.navigator.userAgent.includes("Chrome") ? "Chrome" : "Browser"}
                        </span>
                      </div>
                      <span className="active-session-pill">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PRIVACY TAB */}
            {activeTab === "privacy" && (
              <div className="nitro-tab-section">
                <div className="nitro-section-header">
                  <h1>Privacy & data</h1>
                  <p>Manage diagnostic data sharing and account archives.</p>
                </div>

                <div className="nitro-row-cards-list">
                  {/* Share Diagnostics */}
                  <div className="nitro-row-card">
                    <div className="row-card-main">
                      <div className="row-card-info">
                        <span className="row-label">Share anonymous diagnostics</span>
                        <span className="row-value">
                          Allow 9tro Ride to analyze route precision metrics.
                        </span>
                      </div>
                      <label className="nitro-switch">
                        <input
                          type="checkbox"
                          checked={shareDiagnostics}
                          onChange={(e) => {
                            const val = e.target.checked;
                            setShareDiagnostics(val);
                            handleSavePrivacyToggle(val, personalizedOffers);
                          }}
                        />
                        <span className="switch-slider" />
                      </label>
                    </div>
                  </div>

                  {/* Personalization */}
                  <div className="nitro-row-card">
                    <div className="row-card-main">
                      <div className="row-card-info">
                        <span className="row-label">Personalized offers & discounts</span>
                        <span className="row-value">
                          Receive tailored ride vouchers and vendor deals.
                        </span>
                      </div>
                      <label className="nitro-switch">
                        <input
                          type="checkbox"
                          checked={personalizedOffers}
                          onChange={(e) => {
                            const val = e.target.checked;
                            setPersonalizedOffers(val);
                            handleSavePrivacyToggle(shareDiagnostics, val);
                          }}
                        />
                        <span className="switch-slider" />
                      </label>
                    </div>
                  </div>

                  {/* Download Data */}
                  <div className="nitro-row-card">
                    <div className="row-card-main">
                      <div className="row-card-info">
                        <span className="row-label">Download your data</span>
                        <span className="row-value">
                          Get a JSON archive of your 9tro profile and saved locations.
                        </span>
                      </div>
                      <button className="nitro-secondary-btn" onClick={handleDownloadData}>
                        Download
                      </button>
                    </div>
                  </div>

                  {/* Delete Account */}
                  <div className="nitro-row-card danger-row">
                    <div className="row-card-main">
                      <div className="row-card-info">
                        <span className="row-label text-danger">Delete account</span>
                        <span className="row-value text-danger">
                          Permanently delete your 9tro account and remove saved data.
                        </span>
                      </div>
                      <button
                        className="nitro-danger-btn"
                        onClick={() => setShowDeleteModal(true)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {showPhoneModal && (
        <div className="modal-backdrop" onClick={() => setShowPhoneModal(false)}>
          <div className="phone-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="phone-modal-header">
              <h3>Phone number</h3>
              <p>You’ll use this number to get notifications, sign in and recover your account.</p>
            </div>

            <form onSubmit={handleSavePhone} className="phone-modal-form">
              <div className="phone-input-label">Phone number</div>
              <div className="phone-country-input-wrap">
                <div className="country-prefix">
                  <span className="flag-icon">🇳🇬</span>
                  <span className="code-prefix">+234</span>
                </div>
                <input
                  type="tel"
                  className="phone-num-input"
                  value={tempPhone}
                  onChange={(e) => setTempPhone(e.target.value.replace(/\D/g, ""))}
                  placeholder="9026863277"
                  required
                />
              </div>

              <div className="phone-modal-actions">
                <button
                  type="button"
                  className="modal-cancel-btn"
                  onClick={() => setShowPhoneModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="modal-contact-btn" disabled={isSaving}>
                  {isSaving ? "Saving..." : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {supportModalData && (
        <div className="modal-backdrop" onClick={() => setSupportModalData(null)}>
          <div className="support-photo-modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>{supportModalData.title}</h3>
            <p>{supportModalData.message}</p>
            <div className="modal-btn-group">
              <button
                className="modal-contact-btn"
                onClick={() => {
                  alert("Redirecting to 9tro Support...");
                  setSupportModalData(null);
                }}
              >
                Contact support
              </button>
              <button className="modal-cancel-btn" onClick={() => setSupportModalData(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-backdrop" onClick={() => setShowDeleteModal(false)}>
          <div className="delete-modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Account?</h3>
            <p>Are you sure you want to delete your 9tro Ride account? This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button
                className="confirm-delete-btn"
                onClick={() => {
                  alert("Account deletion request submitted.");
                  setShowDeleteModal(false);
                }}
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
